import { faPlus, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useApprovalDomainApprovers } from '@hooks/useApprovalDomainApprovers'
import { useDepartments } from '@hooks/useDepartments'
import { useFacilities } from '@hooks/useFacilities'
import { Tooltip } from '@lib/tooltip'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { Button } from 'components/buttons'
import { Spinner } from 'components/loaders'
import { SimpleTable } from 'components/tables'
import { ReactNode, useId, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TableButton } from './TableButton'

const columnHelper = createColumnHelper<Approver>()

const resolveNames = (
  ids: (string | number)[],
  namesById: Map<string, { name: string }>,
) => {
  if (ids.length === 0) return ''

  const names = ids.map((id) => namesById.get(String(id))?.name)
  if (names.some((name) => name === undefined)) return ''

  return names.join(', ')
}

const TruncateWithEllipsis = ({ children }: { children: ReactNode }) => {
  const id = `truncate-tooltip-${useId()}`
  return (
    <>
      <div
        data-tooltip-id={id}
        className="max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap"
      >
        {children}
      </div>
      <Tooltip id={id}>{children}</Tooltip>
    </>
  )
}

const NamesCell = ({
  names,
  isLoading,
}: {
  names: string
  isLoading: boolean
}) => {
  const { t } = useTranslation()

  if (names !== '') return <TruncateWithEllipsis>{names}</TruncateWithEllipsis>
  if (isLoading) return <Spinner />

  return (
    <span className="flex items-center gap-1 text-error-500">
      <FontAwesomeIcon
        icon={faTriangleExclamation}
        size="sm"
        fixedWidth={true}
      />
      {t('features.approvalDomainApprovers.invalidEmptySelection')}
    </span>
  )
}

export const ApproversTable = ({
  approvalDomainId,
  onAdd,
  onEdit,
  onDelete,
}: {
  approvalDomainId: string
  onAdd: () => void
  onEdit: (approver: Approver) => void
  onDelete: (approver: Approver) => void
}) => {
  const { t } = useTranslation()
  const { data: facilities = [], isLoading: facilitiesLoading } =
    useFacilities()
  const { data: departments = [], isLoading: departmentsLoading } =
    useDepartments()
  const { data: approvers = [] } = useApprovalDomainApprovers(approvalDomainId)

  const facilitiesById = useMemo(
    () => new Map(facilities.map((f) => [String(f.id), f])),
    [facilities],
  )

  const departmentsById = useMemo(
    () => new Map(departments.map((d) => [String(d.id), d])),
    [departments],
  )

  const facilityNames = (approver: Approver) =>
    approver.allFacilities
      ? t('common.all')
      : resolveNames(approver.facilityIds, facilitiesById)

  const departmentNames = (approver: Approver) =>
    approver.allWorkdayDepartments
      ? t('common.all')
      : resolveNames(approver.workdayDepartmentIds, departmentsById)

  const columns = [
    columnHelper.accessor('user.name', {
      header: t('common.name'),
      id: 'name',
      cell: ({ getValue }) => (
        <TruncateWithEllipsis>{getValue()}</TruncateWithEllipsis>
      ),
    }),
    columnHelper.accessor('user.email', {
      header: t('common.email'),
      id: 'email',
      cell: ({ getValue }) => (
        <TruncateWithEllipsis>{getValue()}</TruncateWithEllipsis>
      ),
    }),
    columnHelper.accessor(facilityNames, {
      id: 'facilities',
      header: t('common.facilities'),
      cell: ({ getValue, row, column }) => {
        /**
         * These two accessors read a *different* query's data than the rows
         * they're given, so react-table's per-row value cache has to be
         * cleared: on a cold load it would otherwise pin whatever the accessor
         * computed before /facilities and /departments arrived, and the names
         * would never appear (#1328).
         *
         * Same internals-poking fix as features/approvals/components/Table.tsx
         * — see the longer note there for why that was deemed acceptable.
         **/
        delete row._valuesCache[column.id]

        return <NamesCell names={getValue()} isLoading={facilitiesLoading} />
      },
    }),
    columnHelper.accessor(departmentNames, {
      id: 'departments',
      header: t('features.approvalDomainApprovers.departments'),
      cell: ({ getValue, row, column }) => {
        delete row._valuesCache[column.id]

        return <NamesCell names={getValue()} isLoading={departmentsLoading} />
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: t('features.approvalDomainApprovers.actions'),
      cell: ({ row }) => {
        const approver = row.original
        return (
          <div className="flex gap-2 items-center">
            <TableButton variant="primary" onClick={() => onEdit(approver)}>
              {t('common.edit')}
            </TableButton>
            |
            <TableButton variant="danger" onClick={() => onDelete(approver)}>
              {t('common.delete')}
            </TableButton>
          </div>
        )
      },
    }),
  ] as ColumnDef<Approver>[]

  return (
    <div className="flex flex-col w-[825px]">
      {approvers.length > 0 ? (
        <SimpleTable
          columns={columns}
          data={approvers}
          defaultSortColumn="name"
        />
      ) : (
        <div className="flex flex-col items-center text-sm gap-4 py-8 text-neutral-700 rounded text-center border border-neutral-200">
          {t('features.approvalDomainApprovers.noApprovers')}
          <Button icon={faPlus} className="text-sm" onClick={onAdd}>
            {t('features.approvalDomainApprovers.addApprovers')}
          </Button>
        </div>
      )}
    </div>
  )
}
